import React, { useEffect, useState, useCallback, useRef } from "react";
import { heroes, HEROES_FONTS_HREF } from "./votes/heroesTokens";

// Announcement pop-up for the Heroes Wanted campaign, shown on the home page.
// Styled with the standalone Heroes Wanted design tokens. Appears once after a
// short delay; "Don't show again" persists the dismissal in localStorage so the
// visitor never sees it again on this browser.

const STORAGE_KEY = "heroesWantedPopupDismissed";
const SHOW_DELAY_MS = 1200;
const HEROES_WANTED_URL = "https://fundacioncaminoacasa.org/heroeswanted";
const VIDEO_SRC = `${process.env.PUBLIC_URL}/assets/video/introo.m4v`;

const copy = {
  eyebrow: "Nueva campaña",
  title: "¡Se buscan héroes!",
  description:
    "Súmate a la campaña contra la trata de personas. ¡Mira los videos y vota!",
  cta: "Ir a Heroes Wanted",
  dismiss: "No mostrar más",
  close: "Cerrar",
  enableSound: "Toca para activar el sonido",
};

export default function HeroesWantedPopup() {
  const [visible, setVisible] = useState(false);
  // Drives the CSS enter transition (opacity + translate) one frame after mount.
  const [entered, setEntered] = useState(false);

  // The video starts muted (browsers only allow autoplay that way). An overlay
  // button lets the visitor enable sound in one tap; it hides once enabled.
  const videoRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  const enableSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    void v.play().catch(() => {});
    setSoundOn(true);
  }, []);

  // Load the Heroes Wanted fonts once, only while the popup is mounted.
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = HEROES_FONTS_HREF;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      // localStorage unavailable (private mode / blocked) — show the pop-up anyway.
    }
    if (dismissed) return;

    const showTimer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [visible]);

  const close = useCallback(() => {
    setEntered(false);
    window.setTimeout(() => setVisible(false), 220);
  }, []);

  const dismissForever = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // Ignore write failures — worst case the pop-up shows again next visit.
    }
    close();
  }, [close]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible, close]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="heroes-popup-title"
      aria-describedby="heroes-popup-desc"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 13000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        backgroundColor: "rgba(13,24,64,0.72)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        opacity: entered ? 1 : 0,
        transition: "opacity 220ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          backgroundColor: heroes.color.ink,
          backgroundImage: `radial-gradient(120% 90% at 50% -10%, ${heroes.color.inkPanel} 0%, ${heroes.color.ink} 55%, ${heroes.color.inkDeep} 100%)`,
          border: `1px solid ${heroes.color.blueBorder}`,
          borderRadius: heroes.radius.card,
          boxShadow: heroes.shadow.cardNavy,
          padding: "52px 32px 32px",
          textAlign: "center",
          fontFamily: heroes.font.body,
          transform: entered
            ? "translateY(0) scale(1)"
            : "translateY(16px) scale(0.98)",
          transition: "transform 240ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <button
          type="button"
          onClick={close}
          aria-label={copy.close}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            borderRadius: heroes.radius.pill,
            color: heroes.color.blueLight,
            fontSize: "20px",
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>

        <span
          style={{
            display: "inline-block",
            color: heroes.color.redBright,
            fontFamily: heroes.font.body,
            fontWeight: 700,
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: heroes.letter.label,
            marginBottom: "14px",
          }}
        >
          {copy.eyebrow}
        </span>

        <h2
          id="heroes-popup-title"
          style={{
            margin: 0,
            color: heroes.color.white,
            fontFamily: heroes.font.display,
            fontSize: "28px",
            lineHeight: 1.1,
            textTransform: "uppercase",
            letterSpacing: heroes.letter.button,
          }}
        >
          {copy.title}
        </h2>

        {/* Intro video: browsers only allow autoplay when muted, so it starts
            silent. An overlay button lets the visitor enable sound in one tap. */}
        <div style={{ position: "relative", marginTop: "20px" }}>
          <video
            ref={videoRef}
            src={VIDEO_SRC}
            autoPlay
            muted
            playsInline
            controls
            preload="auto"
            onVolumeChange={() => {
              const v = videoRef.current;
              if (v) setSoundOn(!v.muted && v.volume > 0);
            }}
            style={{
              display: "block",
              width: "100%",
              aspectRatio: "16 / 9",
              objectFit: "cover",
              borderRadius: "8px",
              border: `1px solid ${heroes.color.blueBorder}`,
              backgroundColor: heroes.color.inkDeep,
            }}
          />
          {!soundOn && (
            <button
              type="button"
              onClick={enableSound}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 14px",
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                backgroundColor: heroes.color.red,
                color: heroes.color.white,
                fontFamily: heroes.font.body,
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: heroes.letter.label,
                boxShadow: heroes.shadow.redGlow,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4.03v8.05A4.5 4.5 0 0016.5 12zM14 3.23v2.06a7 7 0 010 13.42v2.06a9 9 0 000-17.54z" />
              </svg>
              {copy.enableSound}
            </button>
          )}
        </div>

        <p
          id="heroes-popup-desc"
          style={{
            margin: "16px 0 28px",
            color: heroes.color.blueLight,
            fontFamily: heroes.font.body,
            fontSize: "15px",
            lineHeight: 1.6,
          }}
        >
          {copy.description}
        </p>

        <a
          href={HEROES_WANTED_URL}
          onClick={close}
          style={{
            display: "block",
            width: "100%",
            padding: "14px 20px",
            backgroundColor: heroes.color.red,
            color: heroes.color.white,
            fontFamily: heroes.font.body,
            fontWeight: 800,
            fontSize: "15px",
            textTransform: "uppercase",
            letterSpacing: heroes.letter.button,
            textDecoration: "none",
            borderRadius: heroes.radius.button,
            boxShadow: heroes.shadow.redGlow,
            boxSizing: "border-box",
          }}
        >
          {copy.cta}
        </a>

        <button
          type="button"
          onClick={dismissForever}
          style={{
            marginTop: "16px",
            background: "transparent",
            border: "none",
            color: heroes.color.slate,
            fontFamily: heroes.font.body,
            fontSize: "13px",
            fontWeight: 500,
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            cursor: "pointer",
          }}
        >
          {copy.dismiss}
        </button>
      </div>
    </div>
  );
}
